import { ArrowUpRight, Sparkles } from 'lucide-react';

const LOGIC_CARDS = [
    { t: 'Guided you', c: 'It did not leave you guessing. It created a clear path from interest to clarity.' },
    { t: 'Qualified the opportunity', c: 'It collected the information needed to understand your business type, goal, urgency, and current bottleneck.' },
    { t: 'Gave value immediately', c: 'It gave you practical next steps before asking you to book a call.' },
    { t: 'Created a smarter handoff', c: 'If you choose to speak with us, the conversation starts with context instead of basic questions.' },
];

const INDUSTRY_CARDS = [
    { t: 'Dental clinic', c: 'Guide patients from symptoms or treatment interest to the right consultation.' },
    { t: 'Aesthetic clinic', c: 'Help visitors understand which treatment path fits their goal.' },
    { t: 'Consultant or expert', c: 'Qualify leads by need, urgency, and budget before the first call.' },
    { t: 'Course or event provider', c: 'Match visitors to the right programme, workshop, or event.' },
    { t: 'E-commerce brand', c: 'Recommend the right product, bundle, or buying path.' },
];

export function EducationalReveal({ onClose }) {
    return (
        <div
            data-testid="snapshot-educational"
            className="mt-12 pt-10 border-t"
            style={{ borderColor: 'rgba(255,255,255,0.10)' }}
        >
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-400">
                §VII — Reveal
            </p>
            <h3 className="mt-4 font-display font-semibold text-[1.9rem] md:text-[2.6rem] leading-[1.05] tracking-[-0.04em] text-white">
                What you just experienced is{' '}
                <span className="text-gradient-cyan">why most websites fail.</span>
            </h3>

            <div className="mt-6 text-[15.5px] leading-[1.65] text-slate-300 space-y-4 max-w-2xl">
                <p>
                    Most websites let visitors browse and leave. This guided
                    you, identified gaps, and pointed to a clear next step.
                    That's the difference.
                </p>
                <p>
                    The same logic — guided, qualified, and routed toward the
                    right action — is what we build for clinics and service
                    businesses so their visitors stop slipping through the
                    cracks.
                </p>
            </div>

            <ol className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-3">
                {LOGIC_CARDS.map((c, i) => (
                    <li
                        key={c.t}
                        className="list-none p-5 rounded-xl"
                        style={{
                            background: 'rgba(255,255,255,0.025)',
                            border: '1px solid rgba(255,255,255,0.08)',
                        }}
                    >
                        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-400">
                            0{i + 1}
                        </p>
                        <h4 className="mt-2 font-display font-semibold text-[18px] tracking-[-0.025em] text-white">
                            {c.t}
                        </h4>
                        <p className="mt-2 text-[13.5px] leading-[1.6] text-slate-400">
                            {c.c}
                        </p>
                    </li>
                ))}
            </ol>

            <p
                data-testid="snapshot-statement"
                className="mt-10 font-display font-semibold text-[24px] md:text-[34px] leading-[1.1] tracking-[-0.035em] max-w-3xl text-white"
            >
                That is the difference between a normal website and{' '}
                <span className="text-gradient-cyan">a growth system.</span>
            </p>

            <div className="mt-12">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                    Now imagine this, built around your business
                </p>
                <h4 className="mt-3 font-display font-semibold text-[1.4rem] md:text-[1.8rem] tracking-[-0.03em] text-white">
                    Now imagine this logic built around your business.
                </h4>

                <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {INDUSTRY_CARDS.map((c) => (
                        <li
                            key={c.t}
                            className="list-none p-4 rounded-xl"
                            style={{
                                background: 'rgba(255,255,255,0.025)',
                                border: '1px solid rgba(255,255,255,0.08)',
                            }}
                        >
                            <p className="font-display font-semibold text-[15.5px] tracking-[-0.02em] text-white">
                                {c.t}
                            </p>
                            <p className="mt-1.5 text-[13px] leading-[1.55] text-slate-400">
                                {c.c}
                            </p>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="mt-12 flex flex-wrap gap-3">
                <a
                    href="mailto:hello@clearadvance.co?subject=Build%20a%20Growth%20System"
                    data-testid="reveal-build-cta"
                    className="btn btn-primary"
                >
                    <Sparkles className="w-4 h-4" />
                    Fix What's Costing You Clients
                    <ArrowUpRight className="w-4 h-4" />
                </a>
                <a
                    href="mailto:hello@clearadvance.co?subject=Strategy%20Call"
                    data-testid="reveal-book-cta"
                    className="btn btn-ghost"
                >
                    Book a Strategy Call
                </a>
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="reveal-close-cta"
                    className="btn-link self-center"
                >
                    Done for now
                </button>
            </div>
        </div>
    );
}
